<template lang="pug">
.paginated-items(:class="{ empty: !initialized || (!items.length && noFilters) }")
    el-dialog(
        :title="$t('define_columns')"
        :visible.sync="dialogFormVisible"
        :append-to-body="true"
        width="536px"
    )
        el-transfer(
            filterable
            :filter-placeholder="$t('search_columns')"
            :titles="[$t('hidden_columns'), $t('show_columns')]"
            :data="allColumns"
            :value="activeColumnKeys"
            :props="{ key: 'key', label: 'name' }"
            @change="$emit('show-table-columns', $event)"
        )
        span(slot="footer" class="dialog-footer")
            el-button(type="primary" @click="savePaginationSettings") {{ $t('save') }}
    slot(name="top")
    // .table-loading(v-if="loading" class="loading")
    //     img(src="@/assets/puff.svg")
    // .table-content(v-else)
    // .table-content(v-loading="loading" :class="{ empty: items.length == 0 }")
    .table-content.hide-on-empty(v-loading="loading")
        table.table(
            v-if="initialized"
            cellspacing="0"
            cellpadding="0"
            :class="{ striped: !showChildren, 'visible-children': showChildren }"
        )
            thead
                tr.headings
                    th.actions(v-if="!!$scopedSlots.actions")
                        slot(name="actions:th")
                    th.info-before(v-if="!!$scopedSlots['info-before']")
                        slot(name="info-before:th")
                    th(
                        v-for="(column, index) in activeColumns"
                        :class="`col-${column.key}`"
                        :align="column.alignment || 'left'"
                    )
                        a.sortable-label(
                            v-if="column.sortable || sortables.includes(column.key)" href="#"
                            @click.prevent.stop="$emit('change-sort', column.key, sort && sort.field === column.key && sort.direction === 'desc' ? 'asc' : 'desc')"
                        )
                            //- i.el-icon-fa-sort-amount-up.fas(v-if="sort && sort.field === column.key && sort.direction === 'asc'") &nbsp;
                            //- i.el-icon-fa-sort-amount-down.fas(v-else-if="sort && sort.field === column.key && sort.direction === 'desc'") &nbsp;
                            span.active-sort(v-if="sort && sort.field === column.key")
                                i.absolute.gray.el-icon-fa-sort.fas(@click.prevent.stop="$emit('change-sort', column.key, 'desc')") &nbsp;
                                i.top.el-icon-fa-sort-up.fas(v-if="sort.direction === 'asc'" @click.prevent.stop="$emit('change-sort', column.key, 'desc')") &nbsp;
                                i.top.el-icon-fa-sort-down.fas(v-else-if="sort.direction === 'desc'" @click.prevent.stop="$emit('change-sort', column.key, 'asc')") &nbsp;
                            i.el-icon-fa-sort.fas.gray(v-else @click.prevent.stop="$emit('change-sort', column.key, 'desc')") &nbsp;
                            slot(:name="`${column.key}:th`" v-bind:column="column" v-bind:value="column.name")
                                span {{ column.name }}
                        template(v-else)
                            slot(:name="`${column.key}:th`" v-bind:column="column" v-bind:value="column.name")
                                span {{ column.name }}
                    th.info-after(v-if="!!$scopedSlots['info-after']")
                        slot(name="info-after:th")
                //- No transition on this row: the class translated a <tr>,
                //- and a transformed table row renders as a flip in every
                //- browser here. It appears and disappears instead.
                tr.filters(v-if="showFilters")
                    th.actions(v-if="!!$scopedSlots.actions")
                        slot(name="actions:filter")
                    th.info-before(v-if="!!$scopedSlots['info-before']")
                        slot(name="info-before:filter")
                    th(v-for="(column, index) in activeColumns" :class="`col-${column.key}`")
                        pagination-filter(
                            v-if="column.filter"
                            :key="index"
                            :name="column.key"
                            :type="column.filter.type"
                            :options="column.filter.options"
                            :values="filters[column.key]"
                            @change="(key, value, debounce) => $emit('change-filter', key, value, debounce)"
                        )
                            template(v-slot:occupy-space="{ value, from, to, option, options }")
                                slot(
                                    :name="`${column.key}:filter:occupy-space`"
                                    v-bind:value="value"
                                    v-bind:from="from"
                                    v-bind:to="to"
                                    v-bind:option="option"
                                    v-bind:options="options"
                                )
                            template(v-slot:options-template="{ options }")
                                slot(:name="`${column.key}:filter:options-template`" v-bind:options="options")
                            template(v-slot:option-template="{ item }")
                                slot(:name="`${column.key}:filter:option-template`" v-bind:item="item")
                    th.info-after(v-if="!!$scopedSlots['info-after']")
                        slot(name="info-after:filter")
            pagination-table-body(:items="items" :class="{ loading: loading }")
                fragment(slot-scope="{ item, index }" :key="item.id")
                    tr.main-item(:class="[{ odd: index % 2, clickable }, rowClass(item)]")
                        td.actions(v-if="!!$scopedSlots.actions")
                            .actions-wrap
                                slot(name="actions" v-bind:item="item")
                        td.info-before.event-listener(
                            v-if="!!$scopedSlots['info-before']"
                            @mouseover="hover"
                            @mouseout="blur"
                            @click.prevent.stop="resolveRowLink(item)"
                        )
                            .info-before-wrap
                                slot(name="info-before" v-bind:item="item")
                        td.event-listener(
                            v-for="(column, index) in activeColumns"
                            :class="`col-${column.key}`"
                            :align="column.alignment || 'left'"
                            @mouseover="hover"
                            @mouseout="blur"
                            @click.prevent.stop="resolveRowLink(item)"
                        )
                            pagination-column(:item="item" :column-config="column")
                                template(v-slot:default="{ value }")
                                    slot(:name="`${column.key}`" v-bind:item="item" v-bind:value="value")
                                template(v-slot:prefix="{ value }")
                                    slot(:name="`${column.key}:prefix`" v-bind:item="item" v-bind:value="value")
                                template(v-slot:suffix="{ value }")
                                    slot(:name="`${column.key}:suffix`" v-bind:item="item" v-bind:value="value")
                        td.info-after(v-if="!!$scopedSlots['info-after']")
                            .info-after-wrap
                                slot(name="info-after" v-bind:item="item")
                    //- tr.child-item(v-if="showChildren" v-for="child in getChildren(item)" :key="child.id")
                    //-     td(v-if="!!$scopedSlots.actions")
                    //-         slot(name="actions" v-bind:item="item")
                    //-     td(v-for="(column, index) in activeColumns")
                    //-         pagination-column(:item="child" :column-config="column")
                    //- .waiting(v-if="activeColumns.length === 0")
    .pagination-footer.flex-row
        .col
            // :current-page.sync="pageInfo.currentPage"
            el-pagination(
                v-if="pageInfo.total >= 1 && items.length >= 1"
                @size-change="$emit('pagination-size-change', $event)"
                @current-change="$emit('pagination-current-change', $event)"
                :current-page="pageInfo.currentPage"
                :page-size="pageInfo.perPage"
                :page-sizes="variables.paginationSizes"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pageInfo.total"
            )
            p.nothing-found(v-else-if="!loading") {{ $t('pagination.nothingFound') }}
            p.accupy-space(v-else)
        .col.grow.hide-on-empty(v-if="initialized" :xs="24" :lg="6")
            slot(name="footer:empty-space")
        .col.define-columns.hide-on-empty(v-if="initialized" :xs="24" :lg="6")
            el-button(size="mini" @click="dialogFormVisible = true") {{ $t('define_columns') }}
</template>

<i18n>
{
    "de": {
        "The pagination config could not be saved permanently.": "Die Konfiguration konnte nicht dauerhaft gespeichert werden.",
        "Pagination config saved successfully!": "Konfiguration erfolgreich gespeichert!"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { useSetupStore } from '@/stores';
import router from '@/router';
// import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
// import { mapGetters, mapActions } from 'vuex'

import PaginationFilter from '@/components/PaginationFilter';
import PaginationColumn from '@/components/PaginationColumn';
import PaginationTableBody from '@/components/PaginationTableBody';
import Fragment from '@/components/Fragment';

export default {
    name: 'PaginatedItems',
    data() {
        return {
            initialized: false,
            dialogFormVisible: false
        };
    },
    components: {
        PaginationFilter,
        PaginationTableBody,
        Fragment,
        PaginationColumn
    },
    props: {
        scope: {
            type: String,
            // valid values are: base / cp / co
            default: 'base'
        },
        type: {
            type: String,
            required: true
        },
        rowLinkConfig: {
            type: Object,
            default() {
                return {};
            }
        },
        filters: {
            type: Object,
            default() {
                return {};
            }
        },
        sortables: {
            type: Array,
            default() {
                return [];
            }
        },
        sort: {
            type: Object,
            default() {
                return {};
            }
        },
        variables: {
            type: Object,
            default() {
                return {};
            }
        },
        activeColumns: {
            type: Array,
            default() {
                return [];
            }
        },
        allColumns: {
            type: Array,
            default() {
                return [];
            }
        },
        items: {
            type: Array,
            default() {
                return [];
            }
        },
        pageInfo: {
            type: Object,
            default() {
                return {};
            }
        },
        loading: {
            type: Boolean,
            default: false
        },
        showFilters: {
            type: Boolean,
            default: false
        },
        showChildren: {
            type: Boolean,
            default: false
        },
        rowClass: {
            type: Function,
            default: () => ''
        },
        persistSettings: {
            // When false, column selection is applied for the current session only
            // and not saved to the user settings (useful when the backend pagination
            // settings input has no entry for this list type yet).
            type: Boolean,
            default: true
        }
    },
    computed: {
        clickable() {
            return this.$listeners.click || (this.rowLinkConfig.value_path && this.rowLinkConfig.route);
        },
        activeColumnKeys() {
            return _.map(this.activeColumns, 'key');
        },
        noFilters() {
            return _.isEmpty(this.filters);
        }
    },
    watch: {
        loading(value) {
            if (value === false) this.initialized = true;
        }
    },
    methods: {
        getChildren(Item) {
            return Item.articles;
        },
        savePaginationSettings() {
            // The column selection is already applied for the current session via the
            // `show-table-columns` event. When persistence is disabled (e.g. the backend
            // settings input has no entry for this list type), just close the dialog.
            if (!this.persistSettings) {
                this.dialogFormVisible = false;
                return;
            }

            let loading = this.$loading({ target: '.el-dialog' });

            const key = this.type;
            const data = _.map(this.activeColumns, column => {
                return { key: column.key };
            });

            const scope = this.scope;

            const setupStore = useSetupStore();
            setupStore
                .saveActiveColumnKeys({ scope, key, data })
                .then(() => {
                    loading.close();
                    this.dialogFormVisible = false;

                    this.$notify.success({
                        title: this.$t('Pagination config saved successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                })
                .catch(err => {
                    loading.close();
                    this.dialogFormVisible = false;

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);

                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: this.$t('The pagination config could not be saved permanently.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000
                    });
                });
        },
        resolveRowLink(item) {
            if (this.rowLinkConfig.value_path && this.rowLinkConfig.route) {
                const id = _.enhancedGet(item, this.rowLinkConfig.value_path, '');
                const route = `${this.rowLinkConfig.route}${id}`;

                router.push(route).catch((error) => {
                    const message = _.get(error, 'message', '');
                    const name = _.get(error, 'name', '');
                    const isRedirect = name === 'NavigationRedirected'
                        || message.includes('Redirected when going from');
                    const isDuplicated = name === 'NavigationDuplicated';

                    if (!isRedirect && !isDuplicated) {
                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(error);
                    }
                });
            } else {
                this.$emit('click', item);
            }
        },
        hover(event) {
            event.target.closest('tr').classList.add('hover');
        },
        blur(event) {
            event.target.closest('tr').classList.remove('hover');
        }
    },
    mounted() {
        if (!this.loading) this.initialized = true;
    }
};
</script>

<style lang="sass" scoped>

.paginated-items
    background-color: white
    thead tr th
        background-color: $color-selection
        // .el-table-column--selection
        //     .el-checkbox
        //         padding-left: 14px
        //         padding-right: 14px
    .table-top
        background-color: #FFF
        padding: 15px
        border-bottom: 1px solid #ddd
        min-height: 59px
        .col-right
            .el-button
                margin-left: 7px
            .el-select
                margin-left: 7px
    .table-bottom
        min-height: 60px
        background-color: #FFF
        padding: 15px
        border-top: 1px solid #ddd
        // border-bottom: 1px solid #ddd
    .table-loading
        display: block
        // float: left
        height: 40px
        margin: 20px
        img
            float: left
    .el-table th
        vertical-align: top
    .el-pager li.active
        border-bottom: 2px solid
    .actions
        width: 1px
        min-width: 1px !important
        .actions-wrap
            white-space: nowrap
            &:deep() > *:not(:first-child)
                margin-left: 10px
    .table-content
        // min-height: calc(100vh - 262px)
        thead :deep()
            tr.headings > th
                white-space: nowrap
            tr.filters > th
                min-width: 80px
                padding: 5px
                padding-top: 0
                vertical-align: top
                input
                    padding: 0 10px
                    padding-right: 20px
                    border-radius: 14px
                    height: 23px
                    line-height: 23px
                    min-width: 70px
                .el-input__suffix
                    right: 1px
                .from, .to
                    .el-input__suffix
                        // top: -2px
                        right: -1px
                        .el-input__icon
                            line-height: 24px
                .el-select
                    .el-input__suffix
                        top: 0
                        .el-select__caret
                            line-height: 15px
                    .el-tag.el-tag--info
                        border-radius: 10px
                        padding-right: 8px
                        margin-left: 4px
                    .el-select__tags-text
                        line-height: 1
                    input
                        height: 28px
                .single-input
                    input
                        height: 28px
                    .el-input__icon
                        font-size: 16px
                .el-range-editor
                    border: none
                    background: none
                    height: auto
                    line-height: initial
                    width: auto
                    display: block
                    padding: 0 25px
                    position: relative
                    padding-right: 0
                    transition: padding-right .1s ease
                    &.filled:hover
                        padding-right: 25px
                    input
                        // min-width: 90px
                        width: 100%
                        border: 1px solid $--border-color-base
                        text-align: left
                        font-size: 12px
                        padding-right: 10px
                        transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)
                        &:hover
                            border-color: $--color-text-placeholder
                        &:first-of-type
                            margin-bottom: 5px
                    .el-range-separator
                        display: none
                    .el-input__icon
                        height: 100%
                        position: absolute
                        top: 0
                        &.el-range__icon
                            left: 5px
                        &.el-range__close-icon
                            right: 5px
                    &.is-active input
                        border-color: $--color-primary
        tbody
            transition: opacity .3s ease
            &.loading
                opacity: .2
                pointer-events: none
            tr:first-child
                td
                    border-top: 1px solid #dddddd
            tr.child-item
                background: #fafafa
            td ul
                margin: 0
                padding-left: 18px
            tr.clickable
                &.hover
                    // background: #f3f3f3
                    color: $--color-primary
                    td
                        background: lighten($--color-primary, 34%)
                .event-listener
                    cursor: pointer

.paginated-items.empty
    :deep() .hide-on-empty
        display: none

.el-checkbox-group
    label
        text-align: left
.el-transfer-panel
    .el-transfer-panel__header
        label
            text-align: left
.waiting
    min-height: 140px
p.nothing-found
    // color: $--color-warning
    margin: 0
    font-size: 1rem
    line-height: 28px

.paginated-items,
.paginated-items .table-bottom
    border-bottom-left-radius: 5px
    border-bottom-right-radius: 5px

.pagination-footer
    flex-wrap: wrap
    align-items: center
    padding: 10px

.sortable-label
    color: inherit
    text-decoration: none
    > i,
    .active-sort
        display: inline-block
        width: 1em
        height: 1em
        line-height: 1
        vertical-align: middle
        text-align: center
    .active-sort
        position: relative
        i
            position: absolute
            top: 0
            left: 0
            width: 100%
            height: 100%
            line-height: 1
            text-align: center
        .absolute
            z-index: 0
        .top
            z-index: 1
    .gray
        color: $--color-info
        opacity: .3

.el-transfer
    display: flex
    align-items: center
    flex-direction: row-reverse
    :deep()
        .el-icon-arrow-left:before
            content: "\E6E0"
        .el-icon-arrow-right:before
            content: "\E6DE"
</style>
